// ── playerPropSummary — the one description of a player-prop rung (#490) ──
//
// A prop rung is a binary market ("Brandon Sproat (MIL) 6+ Strikeouts"), and
// GameDetail's Player Props tab has always described one by minting a synthetic
// GameRow inline: identity key, YES wording in `awayName`, its complement in
// `homeName`, the one book written into both team slots. That description was
// welded to the table that consumes it, so nothing else could reuse it.
//
// This module is that description, lifted out and typed. GameLinesSection maps
// it back onto its synth (appearance unchanged — the props tab still renders
// through GameRows and keeps its Odds / Vol / Liq / Q / Δ columns), and the
// day-wide prototype's read-only row (#476 → #491) reads the same record. One
// place decides how a rung is identified and worded; two surfaces render it.
//
// Pure by contract: no hooks, no store reads, no fetch. Everything it needs
// arrives as an argument.
//
// What it deliberately does NOT carry:
//   • settled  — derived from live market status (rowIsSettled reads the store
//                imperatively), so it stays a caller-supplied render flag
//   • freshness — already on each VenueBook; re-hoisting it here would give a
//                renderer two places to read the same fact from

import {
  MLB_PROP_STAT_LABELS,
  mlbPropRungLabels,
  type MlbPropStat,
} from '@/lib/kalshiTicker/sports/mlb';
import { propGroupKey, propRungKeyOf } from '@/lib/sport/propSynthKey';
import type { GameRow, MlbPropStatGroup, Sport, VenueBook } from '../ValueDashboard/types';

/** The game context a prop rung needs. A per-game surface already knows all of
 *  this and leaves it implicit; a day-wide one cannot. */
export type PropSummaryGame = Pick<
  GameRow,
  'key' | 'sport' | 'date' | 'awayCode' | 'homeCode'
>;

/** One player-prop rung — one binary market, both venues. */
export interface PlayerPropSummary {
  /**
   * Stable row identity.
   *
   * BYTE-IDENTICAL to the key GameDetail's props tab has always minted, and
   * that is load-bearing rather than cosmetic: nothing type-checks this string,
   * but it is parsed back apart by `propSelection.ts` (trade-panel seeding), by
   * GameLinesSection's sticky `rungKeyByGroup`, and by `parsePropSynthKey` in
   * `lib/sport/propSynthKey` — the shared contract this row now mints through
   * (#566). Change the format and panel seeding plus prop nav break silently —
   * no compile error, no runtime throw, just a row that stops selecting.
   */
  key: string;
  /** "NYY @ BAL" — the context the game page leaves implicit. */
  gameLabel: string;
  sport: Sport;
  /** YYYY-MM-DD. */
  date: string;
  playerName: string;
  /** '' on a Poly-only ladder, which carries no team context. */
  teamCode: string;
  stat: MlbPropStat;
  /** YES resolves at this many or more (Kalshi floor_strike = threshold − 0.5). */
  threshold: number;
  /** "6+ Strikeouts" — the rung without the player. */
  propLabel: string;
  /** What YES means, in words. From the shared wording authority. */
  outcomeLabel: string;
  /** Its complement — what NO means. Repeats the same "N+" token on purpose
   *  (see mlbPropRungLabels) so a trader reads one threshold, not two. */
  notOutcomeLabel: string;
  kalshi: VenueBook | null;
  polymarket: VenueBook | null;
  /** Identity of the player+stat ladder these rungs belong to. Byte-identical
   *  to today's, and parsed by the same consumers as `key`. */
  groupKey: string;
  /** "Sproat (MIL) Strikeouts" — the ladder, without a threshold. */
  groupLabel: string;
  /** "6+" — this rung's position on that ladder. */
  rungLabel: string;
  /** Index of the player within the stat group. GameDetail zebra-stripes a
   *  player's rungs as one band off this. */
  playerIndex: number;
  /** Index of the rung within the player's ladder, ascending. Non-zero means
   *  GameDetail renders the row as a continuation of the one above. */
  rungIndex: number;
}

/**
 * Describe every rung of a stat group, players in group order and rungs
 * ascending — the same traversal order the props tab has always rendered.
 *
 * Either venue may be absent on a rung. An unpaired rung keeps `null` on the
 * missing side rather than borrowing the other venue's book: six of the eight
 * stat families are Kalshi-only forever, and Poly lists only some thresholds on
 * the two it does carry.
 */
export function buildPlayerPropSummaries(
  game: PropSummaryGame,
  group: MlbPropStatGroup,
): PlayerPropSummary[] {
  const statLabel = MLB_PROP_STAT_LABELS[group.stat];
  const gameLabel = `${game.awayCode} @ ${game.homeCode}`;
  const summaries: PlayerPropSummary[] = [];

  group.players.forEach((player, playerIndex) => {
    // Poly-only ladders carry no team context — omit the "()" rather than
    // printing an empty one.
    const team = player.teamCode ? ` (${player.teamCode})` : '';
    const groupKey = propGroupKey(game.key, group.stat, player.playerKey);

    player.rungs.forEach((rung, rungIndex) => {
      const { label, notLabel } = mlbPropRungLabels(
        player.playerName,
        player.teamCode,
        group.stat,
        rung.threshold,
      );
      summaries.push({
        key: propRungKeyOf(groupKey, rung.threshold),
        gameLabel,
        sport: game.sport,
        date: game.date,
        playerName: player.playerName,
        teamCode: player.teamCode,
        stat: group.stat,
        threshold: rung.threshold,
        propLabel: `${rung.threshold}+ ${statLabel}`,
        outcomeLabel: label,
        notOutcomeLabel: notLabel,
        kalshi: rung.kalshi,
        polymarket: rung.polymarket,
        groupKey,
        groupLabel: `${player.playerName}${team} ${statLabel}`,
        rungLabel: `${rung.threshold}+`,
        playerIndex,
        rungIndex,
      });
    });
  });

  return summaries;
}
